import { keepPreviousData, useQuery } from "@tanstack/react-query";
import querystring from "query-string";
import { api } from "@/src/core/api/client";
import { ApiError } from "@/src/core/api/types";
import { toBookingItem } from "@/src/shared/features/booking/bookingDetails";
import { bookingService } from "@/src/shared/features/booking/services";
import type {
  IBookingDetailApiResponse,
  IBookingItem,
  IBookingListApiResponse,
  IBookingListQuery,
  IBookingListResponse,
  IBookingPayload,
  IBookingResponse,
} from "@/src/shared/features/booking/types";

export const bookingKeys = {
  all: ["booking"] as const,
  list: (query: IBookingListQuery) =>
    [...bookingKeys.all, "list", query] as const,
  /** `viewer` separates caches for a signed-in user and a token link. */
  detail: (id: string | number, viewer: string | number | undefined) =>
    [...bookingKeys.all, "detail", String(id), viewer ?? "anon"] as const,
  latestWithHost: (guest: string | number, host: string | number) =>
    [
      ...bookingKeys.all,
      "latestWithHost",
      String(guest),
      String(host),
    ] as const,
};

/** Raw calls (Python). Throw an ApiError on failure. */
export const bookingApi = {
  async getList(query: IBookingListQuery): Promise<IBookingListResponse> {
    const params = querystring.stringify({
      page: query.page,
      per_page: query.limit,
      status: bookingService.statusCodes(query.status),
    });
    const { data } = await api.python.get<IBookingListApiResponse>(
      `/guest/bookings?${params}`,
    );
    return bookingService.toListPage(data, query);
  },

  /** Guest view first; a host opening the same booking (403/404 as guest) gets the host view. */
  async getDetail(id: string | number): Promise<IBookingItem> {
    let response: IBookingDetailApiResponse;
    try {
      ({ data: response } = await api.python.get<IBookingDetailApiResponse>(
        `/guest/bookings/${id}`,
      ));
    } catch (error) {
      if (!(error instanceof ApiError) || ![403, 404].includes(error.status))
        throw error;
      ({ data: response } = await api.python.get<IBookingDetailApiResponse>(
        `/host/bookings/${id}`,
      ));
    }
    if (!response.data) throw new ApiError(404, "Booking not found");
    return toBookingItem(response.data);
  },

  /** A booking request or instant booking (Laravel); the response may carry its conversation_id. */
  async create(payload: IBookingPayload): Promise<IBookingItem> {
    const { data } = await api.laravel.post<IBookingResponse>(
      "/booking",
      payload,
    );
    if (!data.success || !data.data?.id)
      throw new ApiError(200, data.message || "Booking failed");
    return data.data;
  },

  /** The guest's newest booking with a host (Laravel), for reviewing from chat. */
  async getLatestWithHost(
    guest: string | number,
    host: string | number,
  ): Promise<IBookingItem | null> {
    const params = querystring.stringify({ guest, host, limit: 1 });
    const { data } = await api.laravel.get<{ data: IBookingItem[] }>(
      `/booking?${params}`,
    );
    return data.data[0] ?? null;
  },
};

export const useBookingListQuery = (
  query: IBookingListQuery,
  enabled: boolean,
) =>
  useQuery({
    queryKey: bookingKeys.list(query),
    queryFn: () => bookingApi.getList(query),
    enabled,
    placeholderData: keepPreviousData,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

export const useBookingDetailQuery = (
  id: string | number | undefined,
  viewer: string | number | undefined,
  enabled: boolean,
) =>
  useQuery({
    queryKey: bookingKeys.detail(id ?? "none", viewer),
    queryFn: () => bookingApi.getDetail(id as string | number),
    enabled: enabled && id !== undefined,
    placeholderData: keepPreviousData,
  });

export const useLatestBookingWithHostQuery = (
  guest: string | number | undefined,
  host: string | number | null,
  enabled: boolean,
) =>
  useQuery({
    queryKey: bookingKeys.latestWithHost(guest ?? "none", host ?? "none"),
    queryFn: () =>
      bookingApi.getLatestWithHost(
        guest as string | number,
        host as string | number,
      ),
    enabled: enabled && guest !== undefined && host !== null,
  });
